'use client';

import { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Inbox, Brain, TriangleAlert, MapPin, BarChart3, ShieldCheck, Zap, Users, Globe, TrendingUp, ChevronDown, ArrowRight, ArrowUpRight, MessageSquareText, Clock, Activity, Target, Tv, Sparkles } from 'lucide-react';
import BookDemoSection from '@/components/BookDemoSection';
import { BookDemoButton, FaqAccordion } from '@/components/ui';
import { ScrollRevealText } from '@/components/primitives/ScrollRevealText';
import { CapabilityBento } from '@/components/primitives/CapabilityBento';
import styles from './page.module.css';

/* ── DATA — content preserved verbatim from the original page ───── */
import { useTranslation } from "@/hooks/useTranslation";
const STATS = [{
  n: '350+',
  label: 'Enterprise & Government Brands'
}, {
  n: '200M+',
  label: 'Customers Served'
}, {
  n: '80%',
  label: 'Faster Response Times'
}, {
  n: '8–12x',
  label: 'Increase in Connected Customer Profiles'
}];
const CAPABILITIES = [{
  Icon: Inbox,
  color: 'var(--brand)',
  colorLight: 'var(--brand-subtle)',
  colorBorder: 'var(--brand-border)',
  tag: 'Omnichannel Support',
  title: 'Holistic Digital Care',
  desc: 'Your customers do not choose one channel — they use all of them. Holistic Digital Care unifies Social, Email, WhatsApp, Reviews, Chat, Calls and more into a single intelligent inbox, so no conversation slips through.',
  bullets: ['Detect risk and sentiment in real time before your team hits send', 'AI-suggested tone, structure, and escalation paths served to agents instantly', 'Auto-generate empathetic, brand-consistent replies in 200+ languages', 'Smart archive or respond decisions based on urgency, emotion, and past behaviour', 'Achieve 80% faster response times with measurably higher CSAT scores'],
  proof: '25 to 40% reduction in average handle time and a significant lift in first-contact resolution.'
}, {
  Icon: Brain,
  color: 'var(--brand)',
  colorLight: 'var(--brand-subtle)',
  colorBorder: 'var(--brand-border)',
  tag: 'Ticket Intelligence',
  title: 'Intelligent Ticket Management',
  desc: 'Every ticket your team touches is enriched, prioritised, and context-loaded before an agent even opens it. Stop triaging manually. Start resolving.',
  bullets: ['Auto-triage and route tickets based on topic, tone, sentiment, and customer profile', 'Predict urgency, detect escalation intent, and flag anomalies in real time', 'One-click ticket summaries — issue, timeline, customer expectations, all surfaced instantly', 'Cluster similar tickets to spot trending issues and auto-handle standard responses'],
  proof: '40 to 60% reduction in spam and non-actionable tickets. 20 to 30% increase in agent productivity with faster, smarter resolutions across every channel.'
}, {
  Icon: TriangleAlert,
  color: 'var(--brand)',
  colorLight: 'var(--brand-subtle)',
  colorBorder: 'var(--brand-border)',
  tag: 'Crisis Response',
  title: 'Crisis Management',
  desc: 'Locobuzz monitors every channel 24/7 for sentiment spikes, PR risks, and viral narratives — so your team responds with calm and precision, not chaos. Detect the spark before there is a fire.',
  bullets: ['Real-time alerts for keyword spikes, influencer mentions, and trending narratives', 'Auto-tag and route high-risk tickets to the right team instantly', 'Geo-clustered severity scoring — PR, legal, and regional teams know where to act first', 'Track sentiment evolution across channels and regions as a crisis unfolds', 'Command your response with one unified view shared across leadership, comms, and support'],
  proof: 'Teams using Locobuzz for real-time crisis management contain issues hours earlier, with near-zero alert fatigue.'
}, {
  Icon: MapPin,
  color: 'var(--brand)',
  colorLight: 'var(--brand-subtle)',
  colorBorder: 'var(--brand-border)',
  tag: 'Multi-Location CX',
  title: 'Location Intelligence',
  desc: 'For brands with multiple outlets, branches, or dealer networks — Location Intelligence gives you one view of how every location is performing on customer experience, in real time.',
  bullets: ['Aggregate reviews, feedback, and mentions from GMB, App Store, Play Store, and surveys per outlet', 'Detect performance patterns, resolution gaps, and recurring complaints by location', 'Enable local teams to respond while HQ stays in full control of brand voice', 'AI-based escalation, tagging, and workflows for speed and consistency at scale', 'Rich geo-dashboards mapping sentiment trends and outlet-level performance'],
  proof: 'Hyundai manages 900+ dealership locations with brand-safe, consistent responses and real-time MIS visibility from group HQ to forecourt.'
}];
const RESULTS = [{
  n: '40–60%',
  label: 'Reduction in spam and non-actionable tickets',
  desc: 'So agents spend time on what actually matters.'
}, {
  n: '20–30%',
  label: 'Increase in agent productivity',
  desc: 'Through AI-guided assist, smart summaries, and automated routing.'
}, {
  n: '8–12x',
  label: 'Increase in connected customer profiles',
  desc: 'Through CRM integration and unified data.'
}, {
  n: '80%',
  label: 'Faster response times',
  desc: 'Achieved with reduced agent effort and higher CSAT scores.'
}];
const COMMAND_FEATURES = [{
  Icon: BarChart3,
  title: 'Role-Customised Live Dashboards',
  desc: 'Real-time dashboards for CX ops, brand health, and leadership — built and filtered per role, not one size fits all.'
}, {
  Icon: Activity,
  title: 'Volume, Sentiment & SLA at a Glance',
  desc: 'Spot volume spikes, sentiment dips, and SLA breaches the moment they happen — not after the damage is done.'
}, {
  Icon: Users,
  title: 'Agent Performance & Resolution Tracking',
  desc: 'Track agent efficiency, resolution trends, and team throughput automatically — no manual reporting.'
}, {
  Icon: Target,
  title: 'One Source of Truth Across Teams',
  desc: 'Keep marketing, support, and leadership aligned. One live view for everyone — no more version conflicts.'
}];
const INTEGRATIONS = [
  { name: 'Salesforce', logo: '/integrations/salesforce-1.png' },
  { name: 'MS Dynamics', logo: '/integrations/microsoft-dynamics.png' },
  { name: 'Zendesk', logo: '/integrations/zendesk-2.png' },
  { name: 'Freshdesk', logo: '/integrations/freskdesk.png' },
  { name: 'Zoho CRM', logo: '/integrations/zoho-1.png' },
  { name: 'Slack', logo: '/integrations/slack.png' },
  { name: 'Airtable', logo: '/integrations/airtable.png' },
  { name: 'Google Sheets', logo: '/integrations/google-sheet.png' },
];
const BRANDS = ['Indian Army', 'IRCTC', 'Shell', 'Taj Hotels', 'IndiGo', 'Apollo Hospitals'];
const FAQS = [{
  q: 'What is AI-powered customer experience management?',
  a: 'AI-powered customer experience management (CXM) refers to using artificial intelligence to automate, optimise, and personalise how brands handle customer interactions across every channel — including social media, email, chat, and reviews. Locobuzz uses AI to triage tickets, detect sentiment, suggest responses, manage crises, and deliver real-time insights to CX teams.'
}, {
  q: 'How does Locobuzz help CX teams reduce response times?',
  a: 'Locobuzz reduces response times through AI-driven ticket triage, automated routing based on sentiment and urgency, and ResponseGenie™ — which drafts empathetic, brand-consistent replies instantly. Brands using Locobuzz report up to 80% faster response times with no increase in headcount.'
}, {
  q: 'What channels does Locobuzz support for customer service?',
  a: 'Locobuzz supports omnichannel customer service across Social Media (Twitter/X, Instagram, Facebook, LinkedIn, YouTube, TikTok), WhatsApp, Email, Google My Business, App Store & Play Store Reviews, Reddit, and survey/NPS forms — all managed from a single unified inbox.'
}, {
  q: 'Can Locobuzz integrate with our existing CRM or helpdesk?',
  a: 'Yes. Locobuzz offers native CRM and helpdesk integrations with Salesforce, Zendesk, Freshdesk, ServiceNow, Zoho, Oracle, and MS Dynamics — plus 100+ apps via its Integration Hub. Data syncs bi-directionally in real time, with no coding required for standard integrations.'
}, {
  q: 'Is Locobuzz suitable for large enterprise teams?',
  a: "Locobuzz is purpose-built for enterprise scale. It's trusted by 350+ brands across 8 countries — including government organisations, airlines, banks, and hospital networks — handling millions of interactions monthly on a cloud-native, SOC 2 Type II and ISO 27001:2022 certified infrastructure."
}, {
  q: 'How does Locobuzz support crisis management for brands?',
  a: "Locobuzz's SignalSense™ engine monitors all channels 24/7, detecting negative sentiment spikes, influencer mentions, and viral narratives in real time. Alerts are severity-scored and geo-clustered, so the right teams (PR, legal, regional ops) know exactly where and how to respond — hours before a crisis escalates."
}];
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 24
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      damping: 18,
      stiffness: 110,
      mass: 0.9
    }
  }
};
const stagger = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.08
    }
  }
};
const BRAND = 'var(--brand)';
const BRAND_DEEP = 'var(--brand-deep)';
const BRAND_SUB = 'var(--brand-subtle)';
const BRAND_BORDER = 'var(--brand-border)';
const TXT_PRIMARY = 'var(--text-primary)';
const TXT_BODY = 'var(--text-body)';
const TXT_MUTED = 'var(--text-muted)';
const BORDER_SOFT = 'rgba(15, 23, 42, 0.08)';
const BORDER_MED = 'rgba(15, 23, 42, 0.12)';

/* ── PAGE ─────────────────────────────────────────────────────── */
export default function CXTeamsPageClient() {
  const {
    t
  } = useTranslation();
  return <div className={styles.pageShell}>
      <Hero />
      <Capabilities />
      <RealResults />
      <Integrations />
      <CommandCentre />
      <FAQSection />
      <ClosingCTA />
      <BookDemoSection />
    </div>;
}

/* ── HERO — same pattern as for-marketing-teams ──────────────── */
function Hero() {
  const {
    t
  } = useTranslation();
  return <section className={t(styles.hero)}>
      <div aria-hidden className={styles.heroBg}>
        <div className={styles.orb1} />
        <div className={styles.orb2} />
        <div className={styles.dottedGrid} />
      </div>

      <div className={`container ${styles.heroInner}`}>
        <motion.span className={styles.capsule} initial={{
        opacity: 0,
        y: 14
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        type: 'spring',
        damping: 18,
        stiffness: 120
      }}>
          {t("team.cx")}
        </motion.span>

        <motion.h1 className={t(styles.title)} initial={{
        opacity: 0,
        y: 24
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        type: 'spring',
        damping: 16,
        stiffness: 100,
        mass: 1,
        delay: 0.05
      }}>
          {t("solutions_for_cx_teams.turn_every_customer_interaction_int")}{' '}
          <span className={`${styles.titleBlue} serif-accent italic`}>{t("solutions_for_cx_teams.competitive_advantage")}</span>
          <span className={styles.titleDeep}>.</span>
        </motion.h1>
      </div>

      {/* Bottom row — metrics band LEFT + subtitle RIGHT */}
      <div className={`container ${styles.bottomRow}`}>
        <div className={styles.bottomGrid}>
          <motion.div className={styles.metricsBand} initial={{
          opacity: 0,
          x: -30
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          type: 'spring',
          damping: 18,
          stiffness: 110,
          delay: 0.15
        }}>
            <div className={styles.metricsGrid}>
              {STATS.map((stat, i) => <div key={i} className={styles.metric}>
                  <div className={styles.metricN}>{stat.n}</div>
                  <div className={styles.metricLbl}>{t(stat.label)}</div>
                </div>)}
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          type: 'spring',
          damping: 18,
          stiffness: 110,
          delay: 0.2
        }}>
            <p className={t(styles.subtitle)}>
              <span className={styles.subBlue}>{t("solutions_for_cx_teams.cx_teams")}</span> {t("solutions_for_cx_teams.are")} <span className={styles.subDeep}>{t("solutions_for_cx_teams.achieving_more")}</span> {t("solutions_for_cx_teams.with_the_same_resources")}
            </p>

            <p style={{
            fontSize: 13.5,
            color: 'var(--text-muted)',
            lineHeight: 1.65,
            margin: 0
          }}>
              {t("solutions_for_cx_teams.from_aidriven_ticket_triage_to_real")}
            </p>
          </motion.div>
        </div>
      </div>
    </section>;
}

/* ── White scroll-reveal fold ─────────────────────────────────── */
function ScrollRevealFold() {
  const {
    t
  } = useTranslation();
  return <section style={{
    background: 'var(--surface-card)',
    padding: 'var(--section-pad)',
    position: 'relative'
  }}>
      <div className="container">
        <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 3fr',
        gap: 56,
        alignItems: 'start'
      }}>
          <div style={{
          position: 'sticky',
          top: 140,
          paddingTop: 14
        }}>
            <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 12,
            fontWeight: 700,
            color: TXT_MUTED,
            letterSpacing: '0.12em',
            textTransform: 'uppercase'
          }}>
              <span style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: BRAND,
              display: 'block'
            }} />
              {t("solutions_for_cx_teams.why_locobuzz_cx")}
            </span>
          </div>
          <ScrollRevealText mutedColor="rgba(15, 23, 42, 0.18)" revealColor={TXT_PRIMARY} range={['start 0.85', 'start 0.2']} style={{
          fontFamily: 'var(--font-geist), system-ui, sans-serif',
          fontSize: 'clamp(22px, 2.5vw, 32px)',
          fontWeight: 600,
          lineHeight: 1.4,
          letterSpacing: '-0.015em',
          margin: 0,
          maxWidth: 880
        }}>
            {t("solutions_for_cx_teams.with_locobuzz_an_aipowered_customer")}
          </ScrollRevealText>
        </div>
      </div>
    </section>;
}

/* ── CAPABILITIES — pinned, two-column scroll-through stage.
   A tall track (capSection) + a sticky inner (capSticky) pin a full-height
   two-column stage. As the user scrolls, the section's on-screen position maps
   to BOTH the active tab (0..N) AND a 0→1 progress value. LEFT column holds the
   always-visible title + the active spotlight (number / title / one-liner) + a
   progress bar + the tab rail. RIGHT column holds the CapabilityBento, sized to
   fill the column height; it updates IN PLACE (no re-mount) so the scroll-
   through stays smooth. CX-specific: each cap carries a `proof` string surfaced
   as the bento's pointer card ("Proof Point"), and `tag` maps to the bento's
   `sub`. Clicking a rail tab smooth-scrolls (via Lenis) to that tab.
   On ≤900px the pin is disabled, the columns stack, and tabs are tap-driven
   (see the @media block in page.module.css).
   ── */
function Capabilities() {
  const {
    t
  } = useTranslation();
  const [active, setActive] = useState(0);
  const [progress, setProgress] = useState(0);
  const total = CAPABILITIES.length;
  const cap = CAPABILITIES[active];
  const containerRef = useRef(null);

  /* Normalize CX capability data to the bento's expected shape: `tag` → `sub`
     (small kicker headline). `proof` is passed below as pointerOverride so the
     top-right card becomes a "Proof Point" callout. */
  const normalized = {
    ...cap,
    sub: cap.tag
  };

  /* Active tab + scroll progress from the section's real on-screen position.
     CSS pins the stage (sticky), so the section "freezes" and each scroll step
     advances the tab; past the last tab the page scrolls on, before the first
     it scrolls back up.

     The site's Lenis smooth-scroll moves the page with a CSS transform, so
     window.scrollY stays 0 and native scroll events never fire — that breaks
     framer's useScroll AND scroll listeners. getBoundingClientRect() is the
     only reliable read, so we poll it each animation frame, gated by an
     IntersectionObserver so the loop runs only while the section is in view.
     Disabled ≤900px (section is height:auto there; tabs are tap-driven). */
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    let rafId = 0;
    let lastIdx = -1;
    let lastProg = -1;
    const tick = () => {
      const denom = el.offsetHeight - window.innerHeight;
      if (denom > 0 && !window.matchMedia('(max-width: 900px)').matches) {
        const p = Math.min(1, Math.max(0, -el.getBoundingClientRect().top / denom));
        const idx = Math.min(total - 1, Math.max(0, Math.floor(p * total)));
        if (idx !== lastIdx) { lastIdx = idx; setActive(idx); }
        // Throttle progress updates to meaningful deltas to avoid churn.
        if (Math.abs(p - lastProg) > 0.002) { lastProg = p; setProgress(p); }
      }
      rafId = requestAnimationFrame(tick);
    };
    // Only poll while the section is on/near screen — idles cleanly otherwise.
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        if (!rafId) rafId = requestAnimationFrame(tick);
      } else if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = 0;
      }
    }, { rootMargin: '120px' });
    io.observe(el);
    return () => { io.disconnect(); if (rafId) cancelAnimationFrame(rafId); };
  }, [total]);

  /* Click a tab → smooth-scroll so its slice of the track is centred (the
     scroll handler then sets it active). */
  const goTo = useCallback(i => {
    if (typeof window !== 'undefined' && window.matchMedia('(max-width: 900px)').matches) { setActive(i); return; }
    const el = containerRef.current;
    if (!el) return;
    const totalScroll = el.offsetHeight - window.innerHeight;
    const targetY = el.offsetTop + ((i + 0.5) / total) * totalScroll;
    // Lenis owns scrolling; native window.scrollTo is a no-op while it's active.
    if (window.lenis?.scrollTo) window.lenis.scrollTo(targetY);
    else window.scrollTo({ top: targetY, behavior: 'smooth' });
  }, [total]);

  /* Bar fill: prefer the continuous scroll progress (smooth), but never let it
     read lower than the active tab's share so the bar and the NN/N stay in
     sync even at the static endpoints. */
  const barPct = Math.max(progress, (active + 1) / total) * 100;

  return <section id="capabilities" ref={containerRef} className={styles.capSection}>
      <div aria-hidden className={styles.capDots} />

      {/* Two-column stage pins together so the title stays on screen the whole
          time the tabs advance. */}
      <div className={styles.capSticky}>
        <div className={`container ${styles.capInner}`}>
          {/* Full-width header row — sits across the top, above the two columns */}
          <div className={styles.capHeaderBlock}>
            <SectionPill>Core Capabilities</SectionPill>
            <h2 className={styles.capTitle}>{t("solutions_for_cx_teams.the_stack_built_for_the_next_era_of")}</h2>
            <p className={styles.capSub}>Five integrated capability layers — each one solving a distinct CX challenge, all working together from a single intelligent platform.</p>
          </div>

          <div className={styles.capStage}>

            {/* ── LEFT column — spotlight + progress + rail ───── */}
            <div className={styles.capLeft}>
              {/* Active capability spotlight — animates the swap on change */}
              <div className={styles.capSpotlight}>
                {/* Keyed crossfade — entering/exiting children overlap (no
                    mode="wait"), so the swap never blocks on the old child's
                    exit transition completing. */}
                <AnimatePresence initial={false}>
                  <motion.div
                    key={active}
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -14 }}
                    transition={{ type: 'spring', damping: 20, stiffness: 220, mass: 0.7 }}
                    className={styles.capSpotInner}
                  >
                    <span className={styles.capSpotNum}>{String(active + 1).padStart(2, '0')}</span>
                    <h3 className={styles.capSpotTitle}>{t(cap.title)}</h3>
                    <p className={styles.capSpotSub}>{cap.tag}</p>
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Progress: NN / N + thin fill bar */}
              <div className={styles.capProgress}>
                <div className={styles.capProgressMeta}>
                  <span className={styles.capPagCurrent}>{String(active + 1).padStart(2, '0')}</span>
                  <span className={styles.capPagDivider}>/</span>
                  <span className={styles.capPagTotal}>{total}</span>
                </div>
                <div className={styles.capBarTrack} role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(barPct)}>
                  <motion.div
                    className={styles.capBarFill}
                    animate={{ width: `${barPct}%` }}
                    transition={{ type: 'spring', damping: 26, stiffness: 180, mass: 0.6 }}
                  />
                </div>
              </div>

              {/* Tab rail — compact highlighted progress list */}
              <nav aria-label={t("solutions_for_cx_teams.capabilities")} className={styles.capRail}>
                {CAPABILITIES.map((c, i) => {
                const isActive = i === active;
                const isDone = i < active;
                return <button key={c.title} type="button" onClick={() => goTo(i)} aria-current={isActive ? 'true' : undefined} className={`${styles.capRailItem} ${isActive ? styles.capRailItemActive : ''} ${isDone ? styles.capRailItemDone : ''}`}>
                      <span className={styles.capRailNum}>{String(i + 1).padStart(2, '0')}</span>
                      <span className={styles.capRailLabel}>{t(c.title)}</span>
                    </button>;
              })}
              </nav>
            </div>

            {/* ── RIGHT column — bento fills the available height ──────── */}
            <div className={styles.capRight}>
              {/* No key — the bento updates in place as you scroll instead of
                  re-mounting + re-running its entrance animation each tab. CX
                  passes `proof` as the pointer card override. */}
              <CapabilityBento capability={normalized} deckIndex={active} pointerOverride={cap.proof} pointerLabel="Proof Point" />
            </div>

          </div>
        </div>
      </div>

    </section>;
}

/* ── Real Results — 4-card grid + brands strip + tagline below ──── */
function RealResults() {
  const {
    t
  } = useTranslation();
  return <section id="results" style={{
    padding: 'var(--section-pad)',
    background: 'var(--surface-card)'
  }}>
      <div className="container">
        <SectionHeader pill="Real Results" title={t("solutions_for_cx_teams.measured_by_real_teams")} sub="Trusted by brands that cannot afford a gap between customer-centricity in policy and customer-centricity in practice." />

        <motion.div initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} variants={stagger} style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 18
      }}>
          {RESULTS.map((r, i) => <motion.div key={i} variants={fadeUp} whileHover={{
          y: -4
        }} style={{
          background: 'var(--surface-card)',
          border: `1px solid ${BORDER_SOFT}`,
          borderRadius: 22,
          padding: 28,
          textAlign: 'center'
        }}>
              <div style={{
            fontFamily: 'var(--font-geist), system-ui, sans-serif',
            fontSize: 38,
            fontWeight: 800,
            letterSpacing: '-0.03em',
            lineHeight: 1,
            marginBottom: 10,
            background: 'linear-gradient(135deg, var(--brand) 0%, var(--brand-deep) 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
                {r.n}
              </div>
              <div style={{
            fontSize: 12,
            fontWeight: 800,
            color: TXT_PRIMARY,
            marginBottom: 6,
            textTransform: 'uppercase',
            letterSpacing: '0.08em'
          }}>{t(r.label)}</div>
              <div style={{
            fontSize: 13,
            color: TXT_BODY,
            lineHeight: 1.6
          }}>{t(r.desc)}</div>
            </motion.div>)}
        </motion.div>

      </div>
    </section>;
}

/* ── Integrations — logo grid ─────────────────────────────────── */
function Integrations() {
  const {
    t
  } = useTranslation();
  return <section id="integrations" style={{
    padding: 'var(--section-pad)',
    background: 'var(--surface-section-soft)'
  }}>
      <div className="container">
        <SectionHeader pill="Integrations" title={t("solutions_for_cx_teams.plug_into_the_tools_your_team_alrea")} sub="Locobuzz connects natively with your existing stack — no heavy IT lift, no data silos. CRM integrations, helpdesk connectors, and workflow automation tools all work bi-directionally, in real time." />

        <motion.div initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} variants={stagger} style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 16,
        maxWidth: 920,
        margin: '0 auto 28px'
      }}>
          {INTEGRATIONS.map((int, i) => <motion.div key={i} variants={fadeUp} whileHover={{
          y: -3
        }} style={{
          background: 'var(--surface-card)',
          border: `1px solid ${BORDER_SOFT}`,
          borderRadius: 16,
          padding: '22px 16px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 12
        }}>
              <img
                src={int.logo}
                alt={int.name}
                loading="lazy"
                decoding="async"
                style={{
                  height: 36,
                  width: 'auto',
                  maxWidth: '80%',
                  objectFit: 'contain'
                }}
                onError={e => { e.currentTarget.style.display = 'none'; }}
              />
              <span style={{
                fontSize: 12.5,
                fontWeight: 700,
                color: TXT_PRIMARY
              }}>{int.name}</span>
            </motion.div>)}
        </motion.div>

        <p style={{
        textAlign: 'center',
        fontSize: 13,
        color: TXT_MUTED,
        maxWidth: 560,
        margin: '0 auto',
        lineHeight: 1.6
      }}>
          {t("solutions_for_cx_teams.standard_integrations_custom_builds")}
        </p>
      </div>
    </section>;
}

/* ── Command Centre — left features + right live mock card ─────── */
function CommandCentre() {
  const {
    t
  } = useTranslation();
  return <section id="command" style={{
    padding: 'var(--section-pad)',
    background: 'var(--surface-card)',
    position: 'relative',
    overflow: 'hidden'
  }}>
      <div aria-hidden style={{
      position: 'absolute',
      inset: 0,
      backgroundImage: 'radial-gradient(rgba(0, 152, 255,0.05) 1px, transparent 1px)',
      backgroundSize: '32px 32px',
      pointerEvents: 'none'
    }} />
      <div className="container" style={{
      position: 'relative'
    }}>
        <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1.05fr',
        gap: 64,
        alignItems: 'center'
      }}>
          <motion.div initial="hidden" whileInView="show" viewport={{
          once: true
        }} variants={fadeUp}>
            <SectionPill>{t("plat.command")}</SectionPill>
            <h2 style={sectionTitleStyle}>{t("solutions_for_cx_teams.your_cx_war_room_live_and_unified")}</h2>
            <p style={{
            fontSize: 17,
            color: TXT_BODY,
            lineHeight: 1.7,
            marginTop: 14,
            marginBottom: 32,
            maxWidth: 480
          }}>
              {t("solutions_for_cx_teams.cx_leaders_and_brand_heads_get_live")}
            </p>

            <motion.div initial="hidden" whileInView="show" viewport={{
            once: true
          }} variants={stagger} style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 12
          }}>
              {COMMAND_FEATURES.map((f, i) => <motion.div key={i} variants={fadeUp} style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 14,
              padding: '16px 18px',
              background: 'var(--surface-section-soft)',
              border: `1px solid ${BORDER_SOFT}`,
              borderRadius: 14
            }}>
                  <div style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                background: BRAND_SUB,
                border: `1px solid ${BRAND_BORDER}`,
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0
              }}>
                    <f.Icon size={17} color="var(--brand)" strokeWidth={2.2} />
                  </div>
                  <div>
                    <h3 style={{
                  fontFamily: 'var(--font-geist), system-ui, sans-serif',
                  fontWeight: 700,
                  fontSize: 15,
                  color: TXT_PRIMARY,
                  marginBottom: 3
                }}>{t(f.title)}</h3>
                    <p style={{
                  fontSize: 13.5,
                  color: TXT_BODY,
                  lineHeight: 1.6,
                  margin: 0
                }}>{t(f.desc)}</p>
                  </div>
                </motion.div>)}
            </motion.div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 40,
          scale: 0.96
        }} whileInView={{
          opacity: 1,
          y: 0,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          type: 'spring',
          damping: 14,
          stiffness: 90,
          mass: 1
        }}>
            <div style={{
            background: BRAND_DEEP,
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 22,
            overflow: 'hidden'
          }}>
              <div style={{
              background: 'rgba(255,255,255,0.04)',
              borderBottom: '1px solid rgba(255,255,255,0.07)',
              padding: '12px 18px',
              display: 'flex',
              alignItems: 'center',
              gap: 10
            }}>
                <Tv size={14} color="rgba(255,255,255,0.4)" />
                <span style={{
                fontSize: 11,
                fontWeight: 700,
                color: 'rgba(255,255,255,0.65)',
                letterSpacing: '0.04em'
              }}>{t("solutions_for_cx_teams.command_centre_cx_live_view")}</span>
                <span style={{
                marginLeft: 'auto',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                fontSize: 10,
                fontWeight: 800,
                background: 'rgba(127,179,255,0.20)',
                color: 'var(--brand-sky)',
                padding: '3px 10px',
                borderRadius: 999,
                border: '1px solid rgba(127,179,255,0.30)'
              }}>
                  <span style={{
                  width: 5,
                  height: 5,
                  borderRadius: '50%',
                  background: 'var(--brand-sky)',
                  display: 'block'
                }} />
                  {t("solutions_for_cx_teams.live")}
                </span>
              </div>

              <div style={{
              padding: 18,
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 12
            }}>
                {[{
                label: 'Avg Handle Time',
                val: '4.2m',
                delta: '↓ 38%',
                pos: true
              }, {
                label: 'CSAT Score',
                val: '91%',
                delta: '↑ 12',
                pos: true
              }, {
                label: 'Open Tickets',
                val: '247',
                delta: '↑ 18',
                pos: false
              }, {
                label: 'FCR Rate',
                val: '78%',
                delta: '↑ 9',
                pos: true
              }].map((m, i) => <div key={i} style={{
                padding: '14px 16px',
                borderRadius: 12,
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)'
              }}>
                    <div style={{
                  fontSize: 10,
                  fontWeight: 800,
                  color: 'rgba(255,255,255,0.42)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  marginBottom: 6
                }}>{t(m.label)}</div>
                    <div style={{
                  fontSize: 24,
                  fontWeight: 800,
                  color: 'var(--text-inverse)',
                  letterSpacing: '-0.03em',
                  lineHeight: 1
                }}>{m.val}</div>
                    <div style={{
                  fontSize: 11,
                  color: m.pos ? 'var(--brand-sky)' : '#FCA5A5',
                  marginTop: 4,
                  fontWeight: 700
                }}>{m.delta}</div>
                  </div>)}
              </div>

              <div style={{
              margin: '0 18px 18px',
              display: 'flex',
              flexDirection: 'column',
              gap: 8
            }}>
                {[{
                label: 'SLA Compliance — Tier 1',
                val: '96%',
                pct: 96
              }, {
                label: 'SLA Compliance — Tier 2',
                val: '74%',
                pct: 74
              }, {
                label: 'Auto-resolved (AI)',
                val: '61%',
                pct: 61
              }].map((bar, i) => <div key={i} style={{
                padding: '10px 12px',
                borderRadius: 10,
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.06)'
              }}>
                    <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: 6
                }}>
                      <span style={{
                    fontSize: 10,
                    fontWeight: 600,
                    color: 'rgba(255,255,255,0.55)'
                  }}>{t(bar.label)}</span>
                      <span style={{
                    fontSize: 11,
                    fontWeight: 800,
                    color: 'var(--brand-sky)'
                  }}>{bar.val}</span>
                    </div>
                    <div style={{
                  height: 4,
                  background: 'rgba(255,255,255,0.08)',
                  borderRadius: 4,
                  overflow: 'hidden'
                }}>
                      <div style={{
                    height: '100%',
                    width: bar.pct + '%',
                    background: 'var(--brand-sky)',
                    borderRadius: 4
                  }} />
                    </div>
                  </div>)}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}

/* ── FAQ ───────────────────────────────────────────────────────── */
function FAQSection() {
  const {
    t
  } = useTranslation();
  return <section id="faq" style={{
    padding: 'var(--section-pad)',
    background: 'var(--surface-section-soft)'
  }}>
      <div className="container">
        <SectionHeader pill="FAQ" title={t("solutions_for_cx_teams.frequently_asked_questions")} sub="AEO & GEO optimised answers for the questions CX teams ask most." />
        <div style={{
        maxWidth: 820,
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 12
      }}>
          <FaqAccordion items={FAQS.map(faq => ({ q: t(faq.q), a: t(faq.a) }))} />
        </div>
      </div>
    </section>;
}

/* ── Closing CTA ───────────────────────────────────────────────── */
function ClosingCTA() {
  const {
    t
  } = useTranslation();
  return <section style={{
    background: 'linear-gradient(135deg, #F4F8FF 0%, #EEF3FF 50%, #F8F9FF 100%)',
    position: 'relative',
    padding: 'var(--section-pad)',
    overflow: 'hidden'
  }}>
      <div aria-hidden style={{
      position: 'absolute',
      inset: 0,
      backgroundImage: 'radial-gradient(rgba(0, 152, 255,0.07) 1px, transparent 1px)',
      backgroundSize: '32px 32px',
      pointerEvents: 'none'
    }} />
      <div aria-hidden style={{
      position: 'absolute',
      width: 620,
      height: 620,
      borderRadius: '50%',
      top: -160,
      left: '50%',
      transform: 'translateX(-50%)',
      background: 'radial-gradient(circle, rgba(0, 152, 255,0.12) 0%, transparent 70%)',
      filter: 'blur(60px)',
      pointerEvents: 'none'
    }} />

      <div className="container" style={{
      position: 'relative',
      zIndex: 2,
      textAlign: 'center',
      maxWidth: 760,
      margin: '0 auto'
    }}>
        <motion.h2 initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} style={{
        fontFamily: 'var(--font-geist), system-ui, sans-serif',
        fontSize: 'clamp(28px,4vw,46px)',
        fontWeight: 800,
        color: TXT_PRIMARY,
        letterSpacing: '-0.025em',
        marginBottom: 16,
        lineHeight: 1.08
      }}>
          {t("solutions_for_cx_teams.ready_to_move_from_customer_centric")}{' '}
          <span style={{
          background: 'linear-gradient(135deg, var(--brand) 0%, var(--brand-deep) 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }}>
            {t("solutions_for_cx_teams.customer_centricity_in_practice")}
          </span>
        </motion.h2>
        <motion.p initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.1
      }} style={{
        fontSize: 17,
        color: TXT_BODY,
        lineHeight: 1.7,
        marginBottom: 12
      }}>
          {t("solutions_for_cx_teams.locobuzz_gives_your_cx_team_the_too")}
        </motion.p>
        <motion.p initial={{
        opacity: 0,
        y: 12
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.15
      }} style={{
        fontSize: 13,
        color: TXT_MUTED,
        marginBottom: 32
      }}>
          {t("solutions_for_cx_teams.30min_personalised_walkthrough_no_c")}
        </motion.p>
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.2
      }} style={{
        display: 'flex',
        justifyContent: 'center',
        gap: 14,
        flexWrap: 'wrap'
      }}>
          <BookDemoButton label={t("solutions_for_cx_teams.see_it_in_action")} />
        </motion.div>
      </div>
    </section>;
}

/* ── Shared section header primitives ─────────────────────────── */
function SectionPill({
  children
}) {
  const {
    t
  } = useTranslation();
  return <span style={{
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    background: BRAND_SUB,
    border: `1px solid ${BRAND_BORDER}`,
    padding: '6px 12px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 800,
    color: BRAND,
    letterSpacing: '0.14em',
    textTransform: 'uppercase'
  }}>
      <Sparkles size={11} strokeWidth={2.5} />
      {children}
    </span>;
}
function SectionHeader({
  pill,
  title,
  sub
}) {
  const {
    t
  } = useTranslation();
  return <div style={{
    textAlign: 'center',
    marginBottom: 52,
    maxWidth: 720,
    marginLeft: 'auto',
    marginRight: 'auto'
  }}>
      <div style={{
      marginBottom: 16
    }}>
        <SectionPill>{pill}</SectionPill>
      </div>
      <h2 style={{
      ...sectionTitleStyle,
      textAlign: 'center'
    }}>{title}</h2>
      <p style={{
      fontSize: 16,
      color: TXT_BODY,
      lineHeight: 1.7,
      marginTop: 14
    }}>{sub}</p>
    </div>;
}
const sectionTitleStyle = {
  fontFamily: 'var(--font-geist), system-ui, sans-serif',
  fontSize: 'clamp(26px, 3.4vw, 40px)',
  fontWeight: 800,
  color: TXT_PRIMARY,
  letterSpacing: '-0.025em',
  lineHeight: 1.15,
  margin: 0
};